/**
 * Admin → Vault tab. Super-admin-only platform-wide sale ledger with
 * destructive delete-transaction and wipe-user-history controls. Pure UI —
 * Admin.jsx owns `vault`, `vaultFilter`, load fn, and delete handlers.
 */
export default function AdminVaultTab({
  vault,
  vaultFilter,
  setVaultFilter,
  loadVault,
  wipeUserHistory,
  deleteTransaction,
}) {
  return (
    <div className="mt-6 space-y-4" data-testid="admin-vault">
      <div className="h57-surface p-5 border-[#FFD700]/40">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-[#FFD700]">→ Sales Vault · Super Admin Only</div>
            <div className="mt-1 font-display font-black uppercase text-lg">Platform-wide sale ledger</div>
            <p className="mt-1 text-xs text-zinc-500 max-w-2xl">
              Every recorded sale on Hunt57, across every user. Delete individual transactions or wipe a user&apos;s entire sales history — this is destructive and cannot be undone.
            </p>
          </div>
          {vault.stats && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-right">
              {[
                ["Total", vault.stats.total_transactions],
                ["Completed", vault.stats.completed],
                ["Disputed", vault.stats.disputed],
                ["Qualifying", vault.stats.qualifying],
              ].map(([k, v]) => (
                <div key={k}>
                  <div className="font-mono text-[10px] text-zinc-500 uppercase tracking-widest">{k}</div>
                  <div className="font-display font-black text-xl text-[#FF5F1F]">{v}</div>
                </div>
              ))}
              <div className="col-span-2 md:col-span-4">
                <div className="font-mono text-[10px] text-zinc-500 uppercase tracking-widest">Gross completed value</div>
                <div className="font-display font-black text-2xl text-[#FFD700]">
                  ${Number(vault.stats.gross_completed_value || 0).toFixed(2)}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Vault filters */}
      <div className="h57-surface p-4 flex flex-wrap items-end gap-3" data-testid="admin-vault-filters">
        <div className="flex-1 min-w-[220px]">
          <label className="h57-label">Filter by user id (seller OR buyer)</label>
          <input className="h57-input font-mono text-xs" data-testid="vault-filter-user"
            value={vaultFilter.user_id} onChange={(e) => setVaultFilter({ ...vaultFilter, user_id: e.target.value })}
            placeholder="uuid…" />
        </div>
        <div>
          <label className="h57-label">Status</label>
          <select className="h57-input" data-testid="vault-filter-status"
            value={vaultFilter.status} onChange={(e) => setVaultFilter({ ...vaultFilter, status: e.target.value })}>
            <option value="">All</option>
            <option value="initiated">Initiated</option>
            <option value="completed">Completed</option>
            <option value="disputed">Disputed</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>
        <button onClick={loadVault} data-testid="vault-apply-filter" className="h57-btn-secondary text-sm">Apply</button>
        {vaultFilter.user_id.trim() && (
          <button onClick={wipeUserHistory} data-testid="vault-wipe-history"
            className="text-xs inline-flex items-center gap-1 border border-red-500 bg-red-500/10 hover:bg-red-500/20 text-red-300 font-mono uppercase tracking-widest py-2 px-3">
            Wipe this user&apos;s history
          </button>
        )}
      </div>

      {/* Ledger table */}
      <div className="h57-surface overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-xs text-zinc-500 uppercase tracking-widest border-b border-white/10">
            <tr>
              <th className="text-left p-3">When</th>
              <th className="text-left p-3">Card</th>
              <th className="text-left p-3">Buyer → Seller</th>
              <th className="text-right p-3">Price</th>
              <th className="text-left p-3">Status</th>
              <th className="text-left p-3">Feedback</th>
              <th className="text-right p-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {vault.transactions.map((t) => (
              <tr key={t.id} className="border-b border-white/5" data-testid={`vault-row-${t.id}`}>
                <td className="p-3 text-zinc-400 text-xs whitespace-nowrap">
                  {new Date(t.created_at).toLocaleDateString()}
                </td>
                <td className="p-3 text-xs max-w-xs truncate">{t.listing_title || "(listing removed)"}</td>
                <td className="p-3 font-mono text-xs">@{t.buyer_username} → @{t.seller_username}</td>
                <td className="p-3 text-right font-display font-bold">${Number(t.sale_price || 0).toFixed(2)}</td>
                <td className="p-3">
                  <span className={`h57-chip !py-1 !px-2 ${
                    t.status === "completed" ? "h57-chip-gold" :
                    t.status === "disputed" ? "!border-red-500/60 !text-red-400" : ""
                  }`}>{t.status}</span>
                  {t.qualifying && <span className="ml-1 h57-chip h57-chip-gold !py-1 !px-2 text-[9px]">Q</span>}
                </td>
                <td className="p-3 text-xs">{t.feedback_stars ? `${t.feedback_stars}★` : "—"}</td>
                <td className="p-3 text-right">
                  <button onClick={() => deleteTransaction(t)} data-testid={`vault-delete-${t.id}`}
                    className="h57-chip !py-1 !px-2 !border-red-500/50 !text-red-400 hover:!border-red-500">
                    Delete
                  </button>
                </td>
              </tr>
            ))}
            {vault.transactions.length === 0 && (
              <tr><td colSpan={7} className="p-8 text-center text-zinc-500 font-mono uppercase text-xs tracking-widest">No transactions match.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
